import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiCalendar, FiTrendingUp, FiStar, FiPlay, FiCpu, FiCode, FiAward, FiBook, FiMusic } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

interface TimelineEvent {
  year: number;
  title: string;
  description: string;
  type: 'personal' | 'industry';
  category: 'career' | 'education' | 'publication' | 'technology' | 'gaming' | 'ai' | 'milestone';
  icon: string;
  details?: string[];
  image?: string;
}

const Timeline: React.FC = () => {
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const [visibleEvents, setVisibleEvents] = useState<Set<number>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  const timelineEvents: TimelineEvent[] = [
    // 2026 - Present
    {
      year: 2026,
      title: "AI-Assisted Software Development Book",
      description: "Writing new book on AI-assisted software development, drawing from experience building 50+ AI-powered projects. Also contributing to the OpenClaw open-source AI agent ecosystem.",
      type: 'personal',
      category: 'publication',
      icon: 'book',
      details: [
        "New book: AI-Assisted Software Development",
        "7 chapters covering practical AI integration",
        "Working with OpenClaw AI agent platform",
        "Continued innovation across 50+ projects"
      ]
    },

    // 2025
    {
      year: 2025,
      title: "50+ AI Projects & Portfolio Launch",
      description: "Explosive year of AI development: built over 50 innovative projects spanning video generation, voice assistants, SaaS platforms, and creative tools. Launched comprehensive portfolio website.",
      type: 'personal',
      category: 'milestone',
      icon: 'star',
      details: [
        "UniversalAgent - breakthrough voice assistant",
        "Multiple AI video generation systems",
        "SaaS platforms: accounting, hotel PMS, CRM",
        "Portfolio website with interactive bio"
      ]
    },
    
    // 2023-2024 - Recent AI Work
    {
      year: 2024,
      title: "AI & Machine Learning Revolution",
      description: "Led multiple AI and ML-powered development projects including video generation, voice assistants, automated content creation, and full-stack SaaS platforms.",
      type: 'personal',
      category: 'career',
      icon: 'cpu',
      details: [
        "InfiniteStoryVideoMaker with vector memory",
        "VidPromptMaker with OpenAI Sora integration",
        "AutoRedact - Google Document AI PII detection",
        "CrewAI Visual Editor for AI agents"
      ]
    },

    {
      year: 2023,
      title: "ChatGPT Public Release",
      description: "OpenAI releases ChatGPT to the public, revolutionizing AI accessibility and sparking the modern AI boom.",
      type: 'industry',
      category: 'ai',
      icon: 'cpu',
      details: [
        "Over 100 million users in 2 months",
        "Sparked widespread AI adoption",
        "Changed how developers work",
        "Democratized AI technology"
      ]
    },

    {
      year: 2023,
      title: "TLM Partners - Technical Director",
      description: "Led technical development of AAA game in esteemed sci-fi franchise universe, managing team of 15 developers.",
      type: 'personal',
      category: 'career',
      icon: 'gamepad',
      details: [
        "Managed 15-person development team",
        "Full oversight of gameplay and technical design",
        "Implemented rigid version control in Perforce",
        "Unreal Engine and C++ development"
      ]
    },

    // 2020-2022
    {
      year: 2021,
      title: "Ford Motor Company - Technical Artist",
      description: "3D architect and technical artist for major automotive projects.",
      type: 'personal',
      category: 'career',
      icon: 'code',
      details: [
        "3D visualization systems",
        "Technical pipeline development",
        "Automotive industry experience"
      ]
    },

    {
      year: 2020,
      title: '"Who We Are" — Published Choral Symphony',
      description: 'Composed "Who We Are," a 14-minute choral work for SATB chorus, children\'s choir, and soprano soloist, published by Cypress Choral Music. A narrative about climate change featuring Humanity, the Children, and Mother Earth.',
      type: 'personal',
      category: 'milestone',
      icon: 'music',
      details: [
        'Published by Cypress Choral Music (CP 2142)',
        'SATB Chorus, Children\'s Choir & Soprano Soloist',
        'Piano or String Orchestra with percussion',
        'Climate change narrative with redemptive optimism'
      ]
    },

    {
      year: 2020,
      title: "PlayStation 5 & Xbox Series X Launch",
      description: "Next-generation consoles launch with ray tracing, 4K gaming, and ultra-fast SSD technology.",
      type: 'industry',
      category: 'gaming',
      icon: 'gamepad',
      details: [
        "Hardware-accelerated ray tracing",
        "4K 120fps gaming capability",
        "Ultra-fast NVMe SSD storage",
        "Backward compatibility revolution"
      ]
    },

    // 2017-2020 - VR Era
    {
      year: 2018,
      title: "Dark Slope Studios - VR Developer",
      description: "Developed location-based VR games including ScaryGirl Mission Maybee and Slugterra Slugslinger VR.",
      type: 'personal',
      category: 'career',
      icon: 'gamepad',
      details: [
        "ScaryGirl Mission Maybee - Free-roam VR",
        "Slugterra Slugslinger - Multiplayer VR",
        "Unreal Engine 4 C++ development",
        "Published for Zero Latency worldwide"
      ]
    },

    {
      year: 2017,
      title: "Cream Productions - VR Lead Developer",
      description: "Senior consultation and development of VR prototypes and experiences, including lifelike Avatar of Dominic Monaghan in VR.",
      type: 'personal',
      category: 'career',
      icon: 'gamepad',
      details: [
        "Lifelike Avatar of Dominic Monaghan in VR",
        "Unity 3D and Unreal Engine development",
        "Managed and mentored team of junior and intermediate developers",
        "Senior consultation on VR prototypes and experiences"
      ]
    },

    {
      year: 2016,
      title: "Pokemon GO Launch",
      description: "Niantic launches Pokemon GO, revolutionizing augmented reality gaming and mobile location-based experiences.",
      type: 'industry',
      category: 'gaming',
      icon: 'gamepad',
      details: [
        "First mainstream AR gaming success",
        "Over 1 billion downloads",
        "Location-based gameplay revolution",
        "Social gaming phenomenon"
      ]
    },

    {
      year: 2016,
      title: "Secret Location - Tech Director",
      description: "Technical direction for VR projects including Mountain Dew VR game and stereoscopic 360 video development.",
      type: 'personal',
      category: 'career',
      icon: 'code',
      details: [
        "VR Game development for Mountain Dew",
        "Steam/Rift/Daydream platforms",
        "MP4 DASH stream plugin development",
        "Unreal Engine source modifications"
      ]
    },

    // 2014-2016 - Mobile Gaming Boom
    {
      year: 2015,
      title: "Augmented Reality Pioneer",
      description: "Built AR experiences before the mainstream: ARKit storybook portal app, Pokemon GO-style grocery store training app, and AR experience for multimillion-dollar Manhattan retail store opening.",
      type: 'personal',
      category: 'career',
      icon: 'cpu',
      details: [
        "ARKit children's book portal experience",
        "AR training app for major grocery chain",
        "Manhattan retail store AR launch",
        "RE3D - 3D real estate visualization"
      ]
    },

    {
      year: 2014,
      title: "Tiny Moosh Games Founded",
      description: "Co-founded and became Director of Tiny Moosh Games, publishing Hopple Hop - Top 25 game in multiple countries.",
      type: 'personal',
      category: 'milestone',
      icon: 'star',
      details: [
        "Published iOS and Android games",
        "Hopple Hop - Top 25 internationally",
        "Unity 3D development",
        "Integrated AdMob and analytics"
      ]
    },

    {
      year: 2014,
      title: "CMR Games - Executive Director",
      description: "Founded CMR Games, overseeing development of ExtroForge RTS/FPS/Sandbox game in Unreal Engine.",
      type: 'personal',
      category: 'career',
      icon: 'gamepad',
      details: [
        "ExtroForge - Alien world RTS/FPS",
        "Unreal Engine development",
        "Led engineers, artists, and writers",
        "Concerto VR Piano simulation"
      ]
    },

    // 2008-2013 - Social Gaming Era
    {
      year: 2011,
      title: "Cartilage Inc. - Technical Director",
      description: "Technical director overseeing digital media arm of commercial production agency.",
      type: 'personal',
      category: 'career',
      icon: 'code',
      details: [
        "HTML5, jQuery, Flash development",
        "iOS/iPad/iPhone applications",
        "WordPress and CMS development",
        "Team and project management"
      ]
    },

    {
      year: 2010,
      title: "Instagram Launch",
      description: "Instagram launches, revolutionizing photo sharing and mobile-first social media experiences.",
      type: 'industry',
      category: 'technology',
      icon: 'trendingup',
      details: [
        "Mobile-first social platform",
        "Filter-based photo sharing",
        "Sold to Facebook for $1B in 2012",
        "Changed social media landscape"
      ]
    },

    // 2008-2010 - Obama Era & Flash Games
    {
      year: 2008,
      title: "Organic - Senior Flash Developer",
      description: "Senior Flash Developer and architect for major brands including Nike and Bank of America.",
      type: 'personal',
      category: 'career',
      icon: 'code',
      details: [
        "Nike social media interactive experiences",
        "Bank of America Flash development",
        "Flash 10 and ActionScript 3",
        "Video and UI development"
      ]
    },

    {
      year: 2007,
      title: "iPhone Launch",
      description: "Apple launches the original iPhone, revolutionizing mobile computing and app development.",
      type: 'industry',
      category: 'technology',
      icon: 'cpu',
      details: [
        "Multi-touch interface revolution",
        "App Store ecosystem (2008)",
        "Mobile web browsing standard",
        "Changed entire tech industry"
      ]
    },

    // 2005-2008 - Oprah & Music Composition
    {
      year: 2005,
      title: "Harpo Productions - Composer",
      description: "Began regular composition work for The Oprah Winfrey Show, continuing until show ended in 2011.",
      type: 'personal',
      category: 'career',
      icon: 'music',
      details: [
        "Composed for Oprah Winfrey Show",
        "6 years of television music",
        "ASCAP/SOCAN registered composer",
        "Network television broadcast"
      ]
    },

    {
      year: 2005,
      title: "YouTube Launch",
      description: "YouTube launches, revolutionizing video sharing and creating the foundation for modern content creation.",
      type: 'industry',
      category: 'technology',
      icon: 'trendingup',
      details: [
        "First major video sharing platform",
        "User-generated content revolution",
        "Sold to Google for $1.65B in 2006",
        "Created influencer economy"
      ]
    },

    // 2003-2008 - CRASH!MEDIA Era
    {
      year: 2005,
      title: "Harpo Productions - Composer",
      description: "Began six years of regular composition for The Oprah Winfrey Show, with music reaching millions of viewers weekly on network television.",
      type: 'personal',
      category: 'career',
      icon: 'music',
      details: [
        "Composed for Oprah Winfrey Show (2005-2011)",
        "ASCAP/SOCAN registered composer",
        "Network television broadcast",
        "Studio 51 Music film & TV library"
      ]
    },

    {
      year: 2004,
      title: "Disney, Nike & MTV Games",
      description: "Created games for the biggest entertainment brands: Pizza Party Pickup became the top-played game on Disney.com, plus games for Nike, MTV Video Music Awards, and Nickelodeon.",
      type: 'personal',
      category: 'career',
      icon: 'gamepad',
      details: [
        "Disney Pizza Party Pickup - #1 on Disney.com",
        "MTV Moonman Blast - Video Music Awards",
        "Nike Catch The Train - Nikelab.com",
        "Nickelodeon SpongeBob board games"
      ]
    },

    {
      year: 2003,
      title: "CRASH!MEDIA Corp - Technical Director",
      description: "Technical project management for major clients: BBC, Sprite, Toyota, Honda, Discovery Channel, Microsoft. Built six-screen interactive display for BBC UK headquarters.",
      type: 'personal',
      category: 'career',
      icon: 'code',
      details: [
        "BBC, Microsoft, Toyota, ESPN clients",
        "Six-screen display at BBC HQ",
        "Flash Ray Engine - licensed 3D engine",
        "Real-time MP3 rendering backend"
      ]
    },

    {
      year: 2003,
      title: "Wolfenstein 3D Flash Recreation",
      description: "Created viral Flash version of Wolfenstein 3D with a custom ray-casting engine. Also recreated Commander Keen. Hundreds of thousands of hits daily.",
      type: 'personal',
      category: 'milestone',
      icon: 'star',
      details: [
        "Viral gaming sensation",
        "Hundreds of thousands of daily hits",
        "Custom Flash Ray Engine - later licensed",
        "Also recreated Commander Keen in Flash"
      ]
    },

    // 2000-2003 - Early Web Gaming
    {
      year: 2001,
      title: "Flash Games Studio Published",
      description: "Published first book 'Flash Games Studio' - pioneering game development literature for web platforms.",
      type: 'personal',
      category: 'publication',
      icon: 'book',
      details: [
        "First of 10+ published books",
        "Pioneering Flash game development",
        "Established expertise in field",
        "International distribution"
      ]
    },

    {
      year: 2000,
      title: "Parlay Games - Lead Flash Developer",
      description: "Lead Flash Developer creating online casino, bingo, and lottery games using ActionScript.",
      type: 'personal',
      category: 'career',
      icon: 'gamepad',
      details: [
        "Casino and lottery game frontends",
        "ActionScript 1.0 and 2.0",
        "JRun servlet integration",
        "Database design and architecture"
      ]
    },

    {
      year: 2000,
      title: "Y2K & Dot-Com Boom Peak",
      description: "Y2K passes without major issues, dot-com boom reaches its peak before the 2001 crash.",
      type: 'industry',
      category: 'technology',
      icon: 'trendingup',
      details: [
        "Internet adoption accelerates",
        "Web development explosion",
        "E-commerce foundation laid",
        "Tech IPO frenzy"
      ]
    },

    // 1995-1999 - University & Early Gaming
    {
      year: 1998,
      title: "University of Toronto - B.Sc Computer Science",
      description: "Graduated with Bachelor of Science in Computer Science, specializing in computer science and mathematics.",
      type: 'personal',
      category: 'education',
      icon: 'award',
      details: [
        "Computer Science and Mathematics",
        "Strong foundation in algorithms",
        "3D mathematics expertise",
        "Prepared for game development career"
      ]
    },

    {
      year: 1995,
      title: "Gray Matter - Game Programmer",
      description: "Game programmer developing console games for Sony PlayStation, Sega Saturn, and Windows 95.",
      type: 'personal',
      category: 'career',
      icon: 'gamepad',
      details: [
        "Sony PlayStation development",
        "Sega Saturn programming",
        "C/C++ and Assembly Language",
        "Published 'Perfect Weapon' on PlayStation"
      ]
    },

    {
      year: 1995,
      title: "PlayStation 1 Launch",
      description: "Sony launches the original PlayStation, revolutionizing 3D gaming and establishing Sony in console market.",
      type: 'industry',
      category: 'gaming',
      icon: 'gamepad',
      details: [
        "First major 3D gaming console",
        "CD-ROM based games",
        "102 million units sold worldwide",
        "Launched iconic franchises"
      ]
    },

    // Early 1990s - High School & Formation
    {
      year: 1993,
      title: "DOOM Release",
      description: "id Software releases DOOM, revolutionizing first-person shooters and multiplayer gaming.",
      type: 'industry',
      category: 'gaming',
      icon: 'gamepad',
      details: [
        "First major FPS sensation",
        "Multiplayer gaming pioneer",
        "3D graphics breakthrough",
        "Modding community foundation"
      ]
    },

    {
      year: 1991,
      title: "High School Computer Club President",
      description: "Became president of high school computer club, leading programming initiatives and game development projects.",
      type: 'personal',
      category: 'milestone',
      icon: 'star',
      details: [
        "Led high school programming team",
        "Organized coding competitions",
        "Taught programming to peers",
        "Early leadership experience"
      ]
    },

    {
      year: 1990,
      title: "Super Nintendo Entertainment System",
      description: "Nintendo launches the SNES, advancing 16-bit gaming with superior graphics and sound.",
      type: 'industry',
      category: 'gaming',
      icon: 'gamepad',
      details: [
        "16-bit graphics advancement",
        "Mode 7 graphics capabilities",
        "Iconic game library",
        "49 million units sold"
      ]
    },

    // Late 1980s - The Beginning
    {
      year: 1985,
      title: "First IBM PC - Halloween Skipped",
      description: "At age 10, received first IBM PC on Halloween - chose programming over trick-or-treating, marking the beginning of a lifelong passion.",
      type: 'personal',
      category: 'milestone',
      icon: 'star',
      details: [
        "Age 10 - life-changing moment",
        "Chose programming over Halloween",
        "First exposure to computing",
        "Beginning of 40-year journey"
      ]
    },

    {
      year: 1985,
      title: "Nintendo Entertainment System Launch",
      description: "Nintendo launches the NES in North America, reviving the video game industry after the 1983 crash.",
      type: 'industry',
      category: 'gaming',
      icon: 'gamepad',
      details: [
        "Revived gaming industry",
        "Introduced quality control",
        "61.9 million units sold",
        "Established Nintendo dominance"
      ]
    }
  ];

  // Sort events by year (most recent first)
  const sortedEvents = [...timelineEvents].sort((a, b) => b.year - a.year);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const year = parseInt(entry.target.getAttribute('data-year') || '0');
          if (entry.isIntersecting) {
            setVisibleEvents(prev => {
              const newSet = new Set(prev);
              newSet.add(year);
              return newSet;
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const timelineElements = document.querySelectorAll('[data-year]');
    if (observerRef.current) {
      timelineElements.forEach(el => observerRef.current?.observe(el));
    }

    return () => {
      if (observerRef.current) {
        timelineElements.forEach(el => observerRef.current?.unobserve(el));
      }
    };
  }, []);

  const getIcon = (iconType: string) => {
    switch (iconType) {
      case 'star': return <FiStar />;
      case 'gamepad': return <FiPlay />;
      case 'cpu': return <FiCpu />;
      case 'code': return <FiCode />;
      case 'award': return <FiAward />;
      case 'book': return <FiBook />;
      case 'music': return <FiMusic />;
      case 'trendingup': return <FiTrendingUp />;
      default: return <FiCalendar />;
    }
  };

  const getEventColor = (event: TimelineEvent) => {
    if (event.type === 'personal') {
      switch (event.category) {
        case 'milestone': return '#FFD700'; // Gold
        case 'career': return '#4A90E2'; // Blue
        case 'education': return '#50E3C2'; // Teal
        case 'publication': return '#F5A623'; // Orange
        default: return '#4A90E2';
      }
    } else {
      switch (event.category) {
        case 'gaming': return '#E94B3C'; // Red
        case 'technology': return '#7ED321'; // Green
        case 'ai': return '#9013FE'; // Purple
        default: return '#BD10E0';
      }
    }
  };

  return (
    <div className="timeline-container" style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '2rem 0',
      position: 'relative'
    }}>
      {/* Header */}
      <motion.div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 2rem',
          marginBottom: '3rem',
          maxWidth: '1200px',
          margin: '0 auto 3rem'
        }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.button
          onClick={() => navigate('/')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: 'rgba(255, 255, 255, 0.15)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '25px',
            color: '#ffffff',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
          }}
          whileHover={{ scale: 1.05, backgroundColor: 'rgba(255, 255, 255, 0.25)' }}
          whileTap={{ scale: 0.95 }}
        >
          <FiArrowLeft size={20} />
          Back to Portfolio
        </motion.button>

        <motion.h1
          style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#ffffff',
            textAlign: 'center',
            textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
            margin: 0
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          Glen's Journey Through Time
        </motion.h1>

        <div style={{ width: '150px' }} /> {/* Spacer for centering */}
      </motion.div>

      {/* Timeline */}
      <div
        ref={timelineRef}
        style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: '0 2rem',
          position: 'relative'
        }}
      >
        {/* Central timeline line */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '0',
            bottom: '0',
            width: '4px',
            background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.2))',
            transform: 'translateX(-50%)',
            borderRadius: '2px',
            zIndex: 1
          }}
        />

        {/* Timeline Events */}
        {sortedEvents.map((event, index) => {
          const isLeft = index % 2 === 0;
          const isVisible = visibleEvents.has(event.year);
          
          return (
            <motion.div
              key={`${event.year}-${index}`}
              data-year={event.year}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '4rem',
                position: 'relative',
                flexDirection: isLeft ? 'row' : 'row-reverse'
              }}
              initial={{ opacity: 0, x: isLeft ? -100 : 100 }}
              animate={isVisible ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              {/* Event Card */}
              <motion.div
                style={{
                  flex: '0 0 45%',
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '15px',
                  padding: '2rem',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                  backdropFilter: 'blur(10px)',
                  cursor: 'pointer',
                  border: `3px solid ${getEventColor(event)}`,
                  position: 'relative'
                }}
                whileHover={{ 
                  scale: 1.03, 
                  boxShadow: '0 15px 40px rgba(0, 0, 0, 0.3)',
                  y: -5
                }}
                onClick={() => setSelectedEvent(event)}
              >
                {/* Event Type Badge */}
                <div
                  style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '20px',
                    background: event.type === 'personal' ? '#4A90E2' : '#E94B3C',
                    color: 'white',
                    padding: '0.3rem 0.8rem',
                    borderRadius: '15px',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  {event.type === 'personal' ? 'Career' : 'Industry'}
                </div>

                {/* Year */}
                <h2 style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: getEventColor(event),
                  margin: '0 0 0.5rem 0'
                }}>
                  {event.year}
                </h2>

                {/* Title */}
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  color: '#333',
                  margin: '0 0 1rem 0',
                  lineHeight: '1.3'
                }}>
                  {event.title}
                </h3>

                {/* Description */}
                <p style={{
                  color: '#666',
                  fontSize: '0.95rem',
                  lineHeight: '1.5',
                  margin: '0 0 1rem 0'
                }}>
                  {event.description}
                </p>

                {/* Details Preview */}
                {event.details && event.details.length > 0 && (
                  <div style={{
                    borderTop: '1px solid #eee',
                    paddingTop: '1rem',
                    marginTop: '1rem'
                  }}>
                    <p style={{
                      color: '#888',
                      fontSize: '0.8rem',
                      margin: '0',
                      fontStyle: 'italic'
                    }}>
                      Click to see {event.details.length} key highlight{event.details.length > 1 ? 's' : ''}...
                    </p>
                  </div>
                )}
              </motion.div>

              {/* Timeline Node */}
              <motion.div
                style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '60px',
                  height: '60px',
                  background: getEventColor(event),
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.2rem',
                  zIndex: 2,
                  border: '4px solid rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                }}
                whileHover={{ scale: 1.2, rotate: 360 }}
                transition={{ duration: 0.3 }}
              >
                {getIcon(event.icon)}
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* Event Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '2rem'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedEvent(null)}
          >
            <motion.div
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '2.5rem',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto',
                position: 'relative',
                border: `4px solid ${getEventColor(selectedEvent)}`
              }}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              transition={{ type: 'spring', damping: 25, stiffness: 500 }}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedEvent(null)}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '20px',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666',
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>

              {/* Event Details */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    background: getEventColor(selectedEvent),
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1.2rem'
                  }}
                >
                  {getIcon(selectedEvent.icon)}
                </div>
                <div>
                  <h2 style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: getEventColor(selectedEvent),
                    margin: 0
                  }}>
                    {selectedEvent.year}
                  </h2>
                  <div
                    style={{
                      background: selectedEvent.type === 'personal' ? '#4A90E2' : '#E94B3C',
                      color: 'white',
                      padding: '0.3rem 0.8rem',
                      borderRadius: '15px',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'inline-block',
                      marginTop: '0.5rem'
                    }}
                  >
                    {selectedEvent.type === 'personal' ? 'Career Milestone' : 'Industry Event'}
                  </div>
                </div>
              </div>

              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#333',
                margin: '0 0 1rem 0'
              }}>
                {selectedEvent.title}
              </h3>

              <p style={{
                color: '#666',
                fontSize: '1rem',
                lineHeight: '1.6',
                margin: '0 0 2rem 0'
              }}>
                {selectedEvent.description}
              </p>

              {/* Detailed List */}
              {selectedEvent.details && selectedEvent.details.length > 0 && (
                <div>
                  <h4 style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: '#333',
                    margin: '0 0 1rem 0'
                  }}>
                    Key Highlights:
                  </h4>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    {selectedEvent.details.map((detail, index) => (
                      <motion.li
                        key={index}
                        style={{
                          padding: '0.75rem 1rem',
                          background: '#f8f9fa',
                          border: `2px solid ${getEventColor(selectedEvent)}20`,
                          borderRadius: '10px',
                          marginBottom: '0.5rem',
                          color: '#555',
                          fontSize: '0.9rem'
                        }}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        • {detail}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Legend */}
      <motion.div
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '15px',
          padding: '1rem',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
          backdropFilter: 'blur(10px)'
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6 }}
      >
        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#333' }}>Legend:</h4>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <div style={{ width: '12px', height: '12px', background: '#4A90E2', borderRadius: '50%' }} />
            <span style={{ fontSize: '0.7rem', color: '#666' }}>Career</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <div style={{ width: '12px', height: '12px', background: '#E94B3C', borderRadius: '50%' }} />
            <span style={{ fontSize: '0.7rem', color: '#666' }}>Industry</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Timeline; 